import { DIFFICULTIES } from "./types.js";
import type { Difficulty, PRNG } from "./types.js";

export interface ShuffleResult {
  shuffled: (string | null)[][];
  minToSolve: number;
}

/**
 * Compute the minimum number of swaps needed to transform `current` into `target`.
 *
 * This is a permutation cycle decomposition problem:
 * - Find all displaced cells (where current !== target)
 * - Build the permutation: for each displaced cell, where does its letter need to go?
 * - Count cycles in the permutation
 * - Minimum swaps = displaced_cells - number_of_cycles
 *
 * Example: A 3-cycle (A→B→C→A) needs 2 swaps. A 2-cycle (A↔B) needs 1 swap.
 */
function computeMinSwaps(
  current: (string | null)[][],
  target: (string | null)[][]
): number {
  // Build a map: for each cell position, what letter is there and what should be there?
  // We need to find where each displaced letter needs to go.
  const rows = current.length;
  const cols = current[0].length;

  // Collect displaced cells: position → { has, needs }
  type Pos = string;
  const displaced = new Map<Pos, { has: string; needs: string }>();

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (current[r][c] !== null && current[r][c] !== target[r][c]) {
        displaced.set(`${r},${c}`, {
          has: current[r][c]!,
          needs: target[r][c]!,
        });
      }
    }
  }

  if (displaced.size === 0) return 0;

  // Build the permutation: for each displaced position, find where its current
  // letter needs to go (i.e., which other displaced position needs this letter).
  // Build adjacency: position → position it should send its letter to
  const posForLetter = new Map<string, Pos[]>();
  for (const [pos, { needs }] of displaced) {
    if (!posForLetter.has(needs)) posForLetter.set(needs, []);
    posForLetter.get(needs)!.push(pos);
  }

  // Count cycles using visited tracking
  const visited = new Set<Pos>();
  let cycles = 0;

  for (const [startPos, { has }] of displaced) {
    if (visited.has(startPos)) continue;

    // Follow the chain: this position has letter X, find where X needs to go
    let currentPos = startPos;
    let currentLetter = has;

    while (!visited.has(currentPos)) {
      visited.add(currentPos);

      // Find a position that needs currentLetter
      const candidates = posForLetter.get(currentLetter);
      if (!candidates || candidates.length === 0) break;

      // Pick the first unvisited candidate
      let nextPos: Pos | null = null;
      for (let i = 0; i < candidates.length; i++) {
        if (!visited.has(candidates[i]) || candidates[i] === startPos) {
          nextPos = candidates[i];
          candidates.splice(i, 1);
          break;
        }
      }

      if (!nextPos || nextPos === startPos) break;

      currentPos = nextPos;
      currentLetter = displaced.get(currentPos)!.has;
    }

    cycles++;
  }

  // Minimum swaps = displaced cells - cycles
  return displaced.size - cycles;
}

/**
 * Creates a shuffled version of the solution grid.
 * Swap count is fixed per difficulty (40%–80% of letter cells, stepping evenly).
 *
 * @param solution The solved grid (null = black cell)
 * @param rng Seeded PRNG for deterministic shuffling
 * @param minSwaps Minimum number of effective swaps in the solution (not shuffle count)
 * @param difficulty Difficulty level — controls shuffle intensity (chill=40%, brutal=68% of cells)
 */
export function shuffleGrid(
  solution: (string | null)[][],
  rng: PRNG,
  minSwaps = 14,
  difficulty: Difficulty = "medium"
): ShuffleResult {
  const shuffled = solution.map((row) => [...row]);

  const cells: [number, number][] = [];
  for (let r = 0; r < solution.length; r++) {
    for (let c = 0; c < solution[r].length; c++) {
      if (solution[r][c] !== null) {
        cells.push([r, c]);
      }
    }
  }

  if (cells.length < 2) {
    return { shuffled, minToSolve: 0 };
  }

  // Shuffle intensity: 40% (chill) → 68% (brutal), sqrt curve softens the top end
  const diffIndex = DIFFICULTIES.indexOf(difficulty);
  const t = diffIndex / (DIFFICULTIES.length - 1);
  const shuffleFraction = 0.4 + Math.sqrt(t) * 0.28;
  const targetOps = Math.floor(cells.length * shuffleFraction);

  for (let attempt = 0; attempt < 20; attempt++) {
    // Reset to solution
    for (let r = 0; r < solution.length; r++) {
      for (let c = 0; c < solution[r].length; c++) {
        shuffled[r][c] = solution[r][c];
      }
    }

    // Perform swaps
    let ops = 0;
    for (let i = 0; i < targetOps * 5 && ops < targetOps; i++) {
      const idxA = rng.nextInt(0, cells.length);
      let idxB = rng.nextInt(0, cells.length - 1);
      if (idxB >= idxA) idxB++;

      const [rA, cA] = cells[idxA];
      const [rB, cB] = cells[idxB];

      if (shuffled[rA][cA] !== shuffled[rB][cB]) {
        const letterA = shuffled[rA][cA];
        const letterB = shuffled[rB][cB];
        if (letterB === solution[rA][cA] || letterA === solution[rB][cB]) {
          continue;
        }
        shuffled[rA][cA] = letterB;
        shuffled[rB][cB] = letterA;
        ops++;
      }
    }

    const min = computeMinSwaps(shuffled, solution);
    if (min >= minSwaps) {
      return { shuffled, minToSolve: min };
    }
  }

  // Fallback: return whatever we have
  let min = computeMinSwaps(shuffled, solution);
  if (min === 0) {
    // Grid is identical to solution — force at least one swap
    // Find any two cells with different letters and swap them
    for (let i = 0; i < cells.length && min === 0; i++) {
      for (let j = i + 1; j < cells.length && min === 0; j++) {
        const [rA, cA] = cells[i];
        const [rB, cB] = cells[j];
        if (shuffled[rA][cA] !== shuffled[rB][cB]) {
          const tmp = shuffled[rA][cA];
          shuffled[rA][cA] = shuffled[rB][cB];
          shuffled[rB][cB] = tmp;
          min = computeMinSwaps(shuffled, solution);
        }
      }
    }
  }
  return { shuffled, minToSolve: Math.max(min, 1) };
}
